import { FunctionComponent, Fragment, useState, useEffect, useRef } from 'react';
import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import { Product, createEmptyProduct } from '@spike/product-model';
import { ProductsStatus } from '@spike/products-action';
import Details from './Details';
import Pricing from './Pricing';
import Inventory, { buildBusinessProductInventory } from './Inventory';
import ResumeDetails from './Details/ResumeDetails';
import ResumePricing from './Pricing/ResumePricing';
import ResumeInventory from './Inventory/ResumeInventory';
import ResumeVendorsAndCost from './VendorsAndCost/ResumeVendorsAndCost';
import { deleteProductThunk, fetchProductsThunk, saveProductThunk } from '@spike/products-action';
import { showError } from '@spike/notifications-action';
import { fetchThunk as fetchVendorsThunk } from 'actions/productVendors/ProductVendorsActions';
import { fetchThunk as fetchCategoriesThunk } from 'actions/productCategories/ProductCategoriesActions';
import { FieldError } from '@spike/model';
import { validateRequired } from 'utils/ValidationUtils';
import VendorsAndCost from './VendorsAndCost';
import ConfirmDialog from '../UI/Dialog';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';
import { useApiClientWrapper } from 'hooks';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';

interface Props {
    product?: Product;
    redirection: boolean;
    onBack: () => void;
    onAddVendor: (product: Product) => void;
    onAddCategory: (product: Product) => void;
    onShowVendor: (id: number, product: Product) => void;
}

enum FormStep {
    ProductDetails = 0,
    Pricing = 1,
    Suppliers = 2,
    Inventory = 3
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            backgroundColor: '#FAFAFA',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden'
        },
        headerContainer: {
            backgroundColor: '#FAFAFA',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            width: '100%',
            flexShrink: 0,
            [theme.breakpoints.down(768)]: {
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '8px',
                flexWrap: 'nowrap',
                gap: '8px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingLeft: '120px',
                paddingRight: '120px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingLeft: '150px',
                paddingRight: '150px'
            }
        },
        bodyContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            backgroundColor: '#FAFAFA',
            flex: 1,
            overflowX: 'hidden',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            [theme.breakpoints.down(768)]: {
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingBottom: '24px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingLeft: '112px',
                paddingRight: '112px',
                paddingBottom: '48px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingLeft: '140px',
                paddingRight: '140px',
                paddingBottom: '60px'
            }
        },
        successContainer: {
            height: '100%',
            backgroundColor: '#fff !important'
        },
        backContainer: {
            display: 'flex',
            position: 'absolute',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            width: '32px',
            top: 0,
            bottom: 0,
            [theme.breakpoints.down(768)]: {
                position: 'static',
                margin: 0,
                width: 'auto',
                height: 'auto',
                fontSize: '20px',
                flexShrink: 0,
                lineHeight: 1
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginLeft: '-72px',
                fontSize: '29px',
                lineHeight: 1
            },
            [theme.breakpoints.up('lg')]: {
                marginLeft: '-90px',
                fontSize: '36px',
                lineHeight: 1
            }
        },
        titleButtonContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            [theme.breakpoints.down(768)]: {
                flex: 1,
                width: 'auto',
                marginTop: 0,
                marginBottom: 0
            },
            [theme.breakpoints.between(768, 'md')]: {
                minWidth: '80%',
                marginTop: '25px',
                marginBottom: '22px'
            },
            [theme.breakpoints.up('lg')]: {
                minWidth: '77%',
                marginTop: '31px',
                marginBottom: '27px'
            }
        },
        titleContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
            minWidth: 0
        },
        title: {
            fontWeight: 600,
            lineHeight: 1.2,
            [theme.breakpoints.down(768)]: {
                fontSize: '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '29px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '36px'
            }
        },
        sectionContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            [theme.breakpoints.down(768)]: {
                marginTop: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginTop: '31px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '39px'
            }
        },
        saveMessage: {
            width: '100%',
            textAlign: 'left',
            [theme.breakpoints.up('lg')]: {
                fontSize: '15px'
            }
        },
        finishButton: {
            [theme.breakpoints.down(768)]: {
                height: '40px',
                width: '100%'
            },
            [theme.breakpoints.between(768, 'md')]: {
                height: '39px',
                width: '136px'
            },
            [theme.breakpoints.up('lg')]: {
                height: '49px',
                width: '170px'
            },
            '& .MuiButton-label': {
                [theme.breakpoints.down('md')]: {
                    fontSize: '13px'
                },
                [theme.breakpoints.up('lg')]: {
                    fontSize: '16px'
                }
            }
        },
        detailsContainer: {
            [theme.breakpoints.down('md')]: {
                marginBottom: '24px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '30px'
            }
        },
        stockContainer: {
            [theme.breakpoints.down('md')]: {
                marginBottom: '96px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '120px'
            }
        },
        saveButtonContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            width: '50%',
            [theme.breakpoints.down(768)]: {
                width: '100%',
                paddingTop: 0,
                justifyContent: 'flex-start'
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingTop: '42px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingTop: '53px'
            }
        },
        unsavedChanges: {
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        },
        availableForCheckoutBox: {
            display: 'flex',
            alignItems: 'flex-start',
            [theme.breakpoints.down(768)]: {
                width: '100%'
            }
        },
        iconCheck: {
            color: '#5E8677',
            flexShrink: 0,
            [theme.breakpoints.down(768)]: {
                fontSize: '14px',
                marginTop: '2px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '16px',
                marginTop: '3px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '20px',
                marginTop: '2px'
            }
        },
        availableText: {
            fontWeight: 700,
            [theme.breakpoints.down(768)]: {
                fontSize: '12px',
                paddingLeft: '8px',
                lineHeight: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '13px',
                paddingLeft: '11px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                paddingLeft: '14px'
            }
        },
        deleteContainer: {
            display: 'flex',
            width: 'auto',
            justifyContent: 'flex-end'
        },
        deleteContent: {
            display: 'flex'
        },
        deleteBox: {
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            marginTop: '20px',
            [theme.breakpoints.down(768)]: {
                paddingBottom: '24px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingBottom: '48px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingBottom: '60px'
            }
        },
        iconDelete: {
            fontWeight: 500,
            color: '#7A7A7A',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                marginRight: '3px',
                marginTop: '2px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                marginRight: '6px',
                marginTop: '3px'
            }
        },
        delete: {
            fontWeight: 500,
            color: '#7A7A7A',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        },
        textDialog: {
            textAlign: 'center',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                lineHeight: '22px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                lineHeight: '27px'
            }
        },
        titleDialog: {
            fontWeight: 600,
            [theme.breakpoints.down('md')]: {
                fontSize: '19px',
                lineHeight: '29px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '24px',
                lineHeight: '36px'
            }
        }
    })
);

enum ProductStep {
    EditStep,
    FinishStep,
    SuccessStep
}

const INVENTORY_TRACKING_METHOD_MESSAGE =
    'Adjust the available stock to 0 before switching inventory tracking methods.';

const isZeroQuantity = (value: number | undefined): boolean => {
    const parsed = Number(value);
    return !Number.isFinite(parsed) || parsed === 0;
};

const hasAvailableStock = (product?: Product): boolean => {
    const businessProduct = product?.businessProduct;
    return !isZeroQuantity(businessProduct?.stock) || !isZeroQuantity(businessProduct?.onHand);
};

export const ProductComponent: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const newProduct: Product = { ...createEmptyProduct(uuid), createdFromScratch: true };

    const [product, setProduct] = useState(props.product ? props.product : newProduct);
    const originalInventoryRef = useRef<{ productId: number; trackBatches: boolean } | null>(null);

    const editedProductId = props.product?.id;
    if (editedProductId != null && originalInventoryRef.current?.productId !== editedProductId) {
        originalInventoryRef.current = {
            productId: editedProductId,
            trackBatches: props.product?.businessProduct?.inventory?.trackBatches === true
        };
    }

    const [errors, setErrors] = useState<Array<FieldError>>([]);

    const [unsavedChanges, setUnsavedChanges] = useState(false);

    const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);

    const [showWarningDeleteProduct, setShowWarningDeleteProduct] = useState(false);

    const isNewProduct = () => {
        return props.product === undefined;
    };

    const loading = useSelector<RootState, boolean>(state => state.products.loading);
    const status = useSelector<RootState, ProductsStatus>(state => state.products.status);
    const previousStatusRef = useRef(status);

    const [step, setStep] = useState<ProductStep>(
        isNewProduct() || props.redirection ? ProductStep.EditStep : ProductStep.FinishStep
    );

    const [lookup, setLookup] = useState(false);

    const vendorsEditRef = useRef<HTMLDivElement>(null);

    const isSuccessStep = () => {
        return step === ProductStep.SuccessStep;
    };

    const [editVendor, setEditVendor] = useState(false);

    const [activeFormStep, setActiveFormStep] = useState<FormStep>(FormStep.ProductDetails);

    useEffect(() => {
        const previousStatus = previousStatusRef.current;
        previousStatusRef.current = status;

        const justSucceeded =
            previousStatus !== status &&
            (status === ProductsStatus.SaveSuccess || status === ProductsStatus.DeleteSuccess);

        if (!justSucceeded) {
            return;
        }

        dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
        dispatch(fetchVendorsThunk());
        dispatch(fetchCategoriesThunk());
        setUnsavedChanges(false);
        props.onBack();
    }, [status]);

    useEffect(() => {
        if (props.product) {
            setProduct(props.product);
        }
    }, [props.product, lookup]);

    useEffect(() => {
        if (editVendor) {
            if (null !== vendorsEditRef.current) {
                vendorsEditRef.current.scrollIntoView();
            }
        }
    }, [editVendor]);

    const cleanErrors = () => {
        setErrors([]);
    };

    const validate = () => {
        const errors: Array<FieldError> = product.createdFromScratch
            ? [
                  validateRequired('name', { ...product }),
                  validateRequired('manufacturer', { ...product }),
                  validateRequired('brand', { ...product })
                  //validateRequired("code", { ...product }),
              ].filter((t): t is FieldError => !!t)
            : [
                  validateRequired('name', { ...product }),
                  validateRequired('manufacturer', { ...product }),
                  validateRequired('brand', { ...product })
              ].filter((t): t is FieldError => !!t);

        setErrors([...errors]);

        return errors;
    };

    const editDetailsHandler = () => {
        setActiveFormStep(FormStep.ProductDetails);
        setStep(ProductStep.EditStep);
    };

    const editPricingHandler = () => {
        setActiveFormStep(FormStep.Pricing);
        setStep(ProductStep.EditStep);
    };

    const editVendorCostHandler = () => {
        setEditVendor(true);
        setActiveFormStep(FormStep.Suppliers);
        setStep(ProductStep.EditStep);
    };

    const editInventoryHandler = () => {
        setActiveFormStep(FormStep.Inventory);
        setStep(ProductStep.EditStep);
    };

    const handleProductDetailsNext = () => {
        cleanErrors();
        const validationErrors = validate();
        if (validationErrors.length === 0) {
            setActiveFormStep(FormStep.Pricing);
        }
    };

    const handlePricingNext = () => {
        setActiveFormStep(FormStep.Suppliers);
    };

    const handleSuppliersNext = () => {
        setActiveFormStep(FormStep.Inventory);
    };

    const handleEditFormStep = (stepIndex: number) => {
        setActiveFormStep(stepIndex as FormStep);
    };

    const handleSaveProduct = () => {
        cleanErrors();
        const validationErrors = validate();
        const originalInventory = originalInventoryRef.current;
        const trackingMethodChanged =
            originalInventory != null &&
            (product.businessProduct?.inventory?.trackBatches === true) !== originalInventory.trackBatches;

        if (trackingMethodChanged && hasAvailableStock(props.product)) {
            dispatch(showError(INVENTORY_TRACKING_METHOD_MESSAGE));
            return;
        }

        if (validationErrors.length === 0) {
            const productAux = { ...product };
            const vendorsAux = product.vendors && product.vendors.filter(v => v.vendor !== undefined);
            productAux.vendors = vendorsAux;

            if (productAux.businessProduct) {
                productAux.businessProduct = {
                    ...productAux.businessProduct,
                    inventory: buildBusinessProductInventory({
                        trackBatches: true,
                        ...productAux.businessProduct.inventory
                    })
                };
            }

            dispatch(saveProductThunk(apiClientWrapper, productAux));
        }
    };

    const handleBack = () => {
        if (unsavedChanges) {
            setShowUnsavedChanges(true);
        } else {
            dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
            dispatch(fetchVendorsThunk());
            dispatch(fetchCategoriesThunk());
            props.onBack();
        }
    };

    const hadleConfirmBack = () => {
        dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
        dispatch(fetchVendorsThunk());
        dispatch(fetchCategoriesThunk());
        props.onBack();
    };

    const changeProductHandler = (product: Product) => {
        setProduct(product);
        setUnsavedChanges(true);
    };

    const hadleChangeVendorCost = (productVendorCost: Product) => {
        setProduct(productVendorCost);
        setUnsavedChanges(true);
    };

    const handleDeleteProduct = () => {
        if (product.id) {
            setShowWarningDeleteProduct(true);
        }
    };

    const handleConfirmDeleteProduct = () => {
        dispatch(deleteProductThunk(apiClientWrapper, product));
    };

    const headerTitle = (
        <Typography className={classes.title}>
            {product?.id === undefined
                ? 'Add Product'
                : isEmpty(product?.businessProduct?.name)
                ? product.name
                : product?.businessProduct?.name}
        </Typography>
    );

    const header = (
        <Fragment>
            <Box
                className={classes.backContainer}
                onClick={handleBack}
            >
                <FontAwesomeIcon icon={faArrowLeft} />
            </Box>
            <Box className={classes.titleButtonContainer}>
                <Box className={classes.titleContainer}>{headerTitle}</Box>
            </Box>
            {step === ProductStep.FinishStep && product.businessProduct?.availableForCheckout && (
                <Box className={classes.saveButtonContainer}>
                    <Box className={classes.availableForCheckoutBox}>
                        <FontAwesomeIcon
                            icon={faCheckCircle}
                            className={classes.iconCheck}
                        />
                        <Typography className={classes.availableText}>Available for checkout</Typography>
                    </Box>
                </Box>
            )}
        </Fragment>
    );

    const editContent = (
        <Fragment>
            <Box className={classes.sectionContainer}>
                <MultiStepForm
                    activeStep={activeFormStep}
                    onEdit={handleEditFormStep}
                    flexibleNavigation={true}
                    steps={[
                        {
                            title: 'Product Details',
                            component: (
                                <Details
                                    product={product}
                                    showLookup={isNewProduct()}
                                    onChange={changeProductHandler}
                                    onAddCategory={() => props.onAddCategory(product)}
                                    onLookup={() => setLookup(true)}
                                    errors={errors}
                                    onNext={handleProductDetailsNext}
                                />
                            )
                        },
                        {
                            title: 'Pricing',
                            component: (
                                <Pricing
                                    product={product}
                                    onChange={changeProductHandler}
                                    onNext={handlePricingNext}
                                />
                            )
                        },
                        {
                            title: 'Suppliers',
                            description: 'Default suppliers will show on the product dashboard.',
                            component: (
                                <div ref={vendorsEditRef}>
                                    <VendorsAndCost
                                        product={product}
                                        onChange={hadleChangeVendorCost}
                                        onShowVendor={vendorId => props.onShowVendor(vendorId, product)}
                                        onNext={handleSuppliersNext}
                                    />
                                </div>
                            )
                        },
                        {
                            title: 'Inventory',
                            description: 'Manage stock levels of this product per locations.',
                            component: (
                                <Inventory
                                    product={product}
                                    saving={loading}
                                    onChange={changeProductHandler}
                                    onSave={handleSaveProduct}
                                />
                            )
                        }
                    ]}
                />
                {((product.id && product.createdFromScratch && !lookup) ||
                    (product.id && !product.createdFromScratch)) && (
                    <Box className={classes.deleteBox}>
                        <Box className={classes.deleteContainer}>
                            <Box
                                className={classes.deleteContent}
                                onClick={handleDeleteProduct}
                            >
                                <FontAwesomeIcon
                                    icon={faTrashAlt}
                                    className={classes.iconDelete}
                                />
                                <Typography className={classes.delete}>Delete Product</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Fragment>
    );

    const finishContent = (
        <Fragment>
            <Box className={classes.sectionContainer}>
                <ResumeDetails
                    onEdit={editDetailsHandler}
                    product={product}
                />
            </Box>
            <Box className={classes.sectionContainer}>
                <ResumePricing
                    onEdit={editPricingHandler}
                    product={product}
                />
            </Box>
            <Box className={classes.sectionContainer}>
                <ResumeVendorsAndCost
                    onEdit={editVendorCostHandler}
                    product={product}
                />
            </Box>
            <Box className={classes.sectionContainer}>
                <ResumeInventory
                    onEdit={editInventoryHandler}
                    product={product}
                />
            </Box>
        </Fragment>
    );

    const stepContent = (): JSX.Element => {
        switch (step) {
            case ProductStep.EditStep: {
                return editContent;
            }
            default: {
                return finishContent;
            }
        }
    };

    return (
        <Fragment>
            <Box className={classes.container}>
                {!isSuccessStep() && <Box className={clsx(classes.headerContainer)}>{header}</Box>}
                <Box
                    className={clsx(classes.bodyContainer, {
                        [classes.successContainer]: isSuccessStep()
                    })}
                >
                    {stepContent()}
                </Box>
                {showUnsavedChanges && (
                    <ConfirmDialog
                        open={true}
                        title="You have unsaved changes."
                        question={
                            <Typography className={classes.unsavedChanges}>
                                Are you sure you want to leave this page without completing or saving changes?
                            </Typography>
                        }
                        cancelButtonLabel="Cancel"
                        confirmButtonLabel="Leave Page"
                        centered
                        onCancel={() => setShowUnsavedChanges(false)}
                        onConfirm={hadleConfirmBack}
                    />
                )}
                {showWarningDeleteProduct && (
                    <ConfirmDialog
                        open={true}
                        centered
                        onCancel={() => setShowWarningDeleteProduct(false)}
                        onConfirm={handleConfirmDeleteProduct}
                        question={
                            <Typography className={classes.titleDialog}>
                                Are you sure you want to delete this product?
                            </Typography>
                        }
                        subQuestion={
                            <Typography className={classes.textDialog}>
                                Deleting product will permanently remove it from your database. You can always recreate
                                it in the future.
                            </Typography>
                        }
                        confirmButtonLabel="Delete"
                        cancelButtonLabel="Nevermind"
                    />
                )}
            </Box>
        </Fragment>
    );
};

export default ProductComponent;
